/**
 * Trip CO₂ Calculator — collapsed trip-inputs summary, overlaid on map.
 *
 * After a successful calculation, the full InputsPanel form
 * (origin/destination autocomplete, mode dropdown, sub-config,
 * round-trip toggle, Calculate button, T&C checkbox) is replaced by
 * this overlay: pills + a small pencil button float on the top edge
 * of the map. The pills and pencil live inside a single `<button>`
 * so clicking *anywhere* on the cluster flips the page back to the
 * editable form view — the pencil is just the visual affordance,
 * not a separately-clickable element.
 *
 * Visual model:
 *   - Pills + pencil icon all share the same height (`pillStyle`'s
 *     4px/10px padding + 12px font-size yields ~24px tall) so they
 *     read as one horizontal row of chips.
 *   - For vehicle modes the route pill and the vehicle-config pill
 *     wrap to two rows (still centred); the pencil stays at the end
 *     of the flow so it ends up beside the second pill.
 *   - A soft drop shadow lifts the cluster off the map surface so
 *     it doesn't get lost on busy tiles.
 *
 * Compositionally this component is just the overlay content — it
 * positions itself absolutely against its nearest `position: relative`
 * ancestor. The page wraps `MapHero` in a relative container and
 * renders this overlay as a sibling inside it.
 */

import React from 'react';
import { MODE_DISPLAY, type ConfigByMode, type TransportMode } from './modes';
import { pillStyle, dotStyle, formatVehiclePill } from './tripPills';

export interface TripSummaryCardProps {
  mode: TransportMode;
  config: ConfigByMode[TransportMode];
  originName: string;
  destinationName: string;
  isRoundTrip: boolean;
  /** Click handler — flips the page to form view (whole cluster fires). */
  onEdit: () => void;
}

// Soft shadow on every chip in the cluster so the row reads as one
// floating "layer" of UI sitting atop the map.
const OVERLAY_SHADOW = '0 4px 14px rgba(15, 23, 42, 0.18)';

const TripSummaryCard: React.FC<TripSummaryCardProps> = ({
  mode,
  config,
  originName,
  destinationName,
  isRoundTrip,
  onEdit,
}) => {
  const { emoji, label } = MODE_DISPLAY[mode];
  const vehiclePill = formatVehiclePill(mode, config);

  // Add the shadow locally so the base `pillStyle` (also used by
  // PerspectiveSection inside a flat bordered card) stays unchanged.
  const chipStyle: React.CSSProperties = {
    ...pillStyle,
    boxShadow: OVERLAY_SHADOW,
  };

  return (
    <button
      type="button"
      onClick={onEdit}
      aria-label="Edit trip"
      title="Edit trip"
      style={{
        // Floats on the top edge of the nearest relative ancestor
        // (the map wrapper). Centred horizontally; flex-wraps when
        // two pills + pencil don't all fit on a single line.
        position: 'absolute',
        top: 12,
        left: 12,
        right: 12,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 6,
        // Reset the native <button> chrome so the chips inside carry
        // the whole visual identity.
        padding: 0,
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        font: 'inherit',
        textAlign: 'left',
      }}
    >
      {vehiclePill ? (
        <>
          <span style={chipStyle}>
            <span>
              {originName} ↔ {destinationName}
            </span>
            <span style={dotStyle}>·</span>
            <span>{isRoundTrip ? 'Round trip' : 'One way'}</span>
          </span>
          <span style={chipStyle}>
            <span>{vehiclePill.emoji}</span>
            <span>{vehiclePill.label}</span>
            <span style={dotStyle}>·</span>
            <span>{vehiclePill.occupancy}</span>
            <span style={dotStyle}>·</span>
            <span>{vehiclePill.fuel}</span>
          </span>
        </>
      ) : (
        <span style={chipStyle}>
          <span>{emoji}</span>
          <span>{label}</span>
          <span style={dotStyle}>·</span>
          <span>
            {originName} ↔ {destinationName}
          </span>
          <span style={dotStyle}>·</span>
          <span>{isRoundTrip ? 'Round trip' : 'One way'}</span>
        </span>
      )}

      {/* Pencil icon chip — same height as the pills so the row
          reads as one horizontal cluster. Not a nested <button>:
          the parent <button> already owns the click. */}
      <span
        aria-hidden
        style={{
          ...chipStyle,
          // Slightly tighter horizontal padding because it's icon-
          // only — keeps the chip from feeling oversized next to
          // the text-heavy pills.
          padding: '4px 8px',
        }}
      >
        ✏️
      </span>
    </button>
  );
};

export default TripSummaryCard;
